import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  type DocumentSnapshot,
  type FirestoreError,
  type QuerySnapshot,
  Timestamp,
} from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import { isXPEvent, type XPEvent } from "@lemonadetraining/contracts";

export type ActivityFeedStatus = "idle" | "loading" | "ready" | "error";

export interface UseActivityFeedResult {
  status: ActivityFeedStatus;
  items: XPEvent[];
  error: Error | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError: Error | null;
  loadMore: () => Promise<void>;
}

type State = Omit<UseActivityFeedResult, "loadMore"> & {
  // True once loadMore has appended at least one page. After that, the live
  // listener's "ready" action stops overwriting hasMore (loadMore owns that
  // signal) and live re-emits merge into the head instead of replacing
  // older pages.
  hasPaginated: boolean;
};

type Action =
  | { type: "loading" }
  | { type: "ready"; items: XPEvent[]; hasMore: boolean }
  | { type: "loadMoreStart" }
  | { type: "loadMoreSuccess"; items: XPEvent[]; hasMore: boolean }
  | { type: "loadMoreError"; error: Error }
  | { type: "error"; error: Error };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "loading":
      return { ...initialState, status: "loading" };
    case "ready": {
      if (!state.hasPaginated) {
        return {
          ...state,
          status: "ready",
          items: action.items,
          error: null,
          hasMore: action.hasMore,
        };
      }
      // After pagination: replace the head, preserve older pages (deduped by
      // id). Don't touch hasMore — that's owned by the loadMore flow once
      // the user has reached page 2+.
      const liveIds = new Set(action.items.map((e) => e.id));
      const tail = state.items.filter((e) => !liveIds.has(e.id));
      return {
        ...state,
        status: "ready",
        items: [...action.items, ...tail],
        error: null,
      };
    }
    case "loadMoreStart":
      return { ...state, loadingMore: true, loadMoreError: null };
    case "loadMoreSuccess": {
      const existingIds = new Set(state.items.map((e) => e.id));
      const merged = state.items.slice();
      for (const ev of action.items) {
        if (!existingIds.has(ev.id)) {
          existingIds.add(ev.id);
          merged.push(ev);
        }
      }
      return {
        ...state,
        items: merged,
        hasMore: action.hasMore,
        hasPaginated: true,
        loadingMore: false,
        loadMoreError: null,
      };
    }
    case "loadMoreError":
      return { ...state, loadingMore: false, loadMoreError: action.error };
    case "error":
      return { ...state, status: "error", error: action.error };
  }
}

const initialState: State = {
  status: "idle",
  items: [],
  error: null,
  hasMore: false,
  loadingMore: false,
  loadMoreError: null,
  hasPaginated: false,
};

function areFeedsEqual(a: XPEvent[], b: XPEvent[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
    if (a[i].xpAwarded !== b[i].xpAwarded) return false;
  }
  return true;
}

/**
 * Subscribes to the athlete's recent XP events at `Users/{uid}/XPEvents`,
 * ordered newest-first, capped at `pageSize` for the live first page.
 * Runtime-validates each doc via `isXPEvent` and drops malformed entries
 * (mirrors the mobile slice). Suppresses dispatches when the new feed has
 * the same ids and xp values as the previous one (Firestore metadata-only
 * re-emits). Cleans up on unmount.
 *
 * `loadMore()` does a one-shot `getDocs` paginated past the current last
 * document; results are appended (de-duped by id) and `hasMore` toggles
 * false once a returned page is shorter than `pageSize`.
 */
export function useActivityFeed(
  uid: string | null | undefined,
  pageSize = 10
): UseActivityFeedResult {
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastItemsRef = useRef<XPEvent[]>([]);
  // Without this, a brand-new athlete (zero XPEvents) gets stuck on
  // `loading` forever: lastItemsRef starts at [], the first snapshot is also
  // [], so areFeedsEqual returns true and we never dispatch `ready`. Force
  // the first dispatch through unconditionally and only then start the
  // equality short-circuit.
  const firstSnapshotRef = useRef(true);
  // Pagination cursor state lives in refs to avoid stale-closure bugs in the
  // exported `loadMore` callback and to keep render state limited to the
  // user-visible payload.
  const lastVisibleRef = useRef<DocumentSnapshot | null>(null);
  const hasMoreRef = useRef(false);
  const uidRef = useRef<string | null | undefined>(uid);
  const loadingMoreRef = useRef(false);
  // Once the user has paginated, the live listener stops touching the cursor
  // — otherwise its full-page emit would reset the cursor back to the page-1
  // boundary and the next loadMore would refetch already-seen rows.
  const hasPaginatedRef = useRef(false);

  useEffect(() => {
    uidRef.current = uid;
    lastVisibleRef.current = null;
    hasMoreRef.current = false;
    hasPaginatedRef.current = false;

    if (!uid) {
      lastItemsRef.current = [];
      firstSnapshotRef.current = true;
      dispatch({ type: "ready", items: [], hasMore: false });
      return;
    }

    dispatch({ type: "loading" });
    firstSnapshotRef.current = true;

    const eventsRef = collection(firebaseDB, "Users", uid, "XPEvents");
    const q = query(eventsRef, orderBy("timestamp", "desc"), limit(pageSize));

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot) => {
        const items: XPEvent[] = [];
        snapshot.docs.forEach((d) => {
          const candidate = { ...d.data(), id: d.id };
          if (isXPEvent(candidate)) items.push(candidate);
        });
        const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
        const hasMore = snapshot.docs.length === pageSize;
        // Cursor + hasMore are owned by loadMore once the user has paginated.
        if (!hasPaginatedRef.current) {
          lastVisibleRef.current = lastDoc as DocumentSnapshot | null;
          hasMoreRef.current = hasMore;
        }
        if (!firstSnapshotRef.current && areFeedsEqual(lastItemsRef.current, items)) {
          return;
        }
        firstSnapshotRef.current = false;
        lastItemsRef.current = items;
        dispatch({ type: "ready", items, hasMore });
      },
      (err: FirestoreError) => {
        dispatch({ type: "error", error: err });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [uid, pageSize]);

  const loadMore = useCallback(async () => {
    const currentUid = uidRef.current;
    if (!currentUid) return;
    if (!hasMoreRef.current) return;
    if (!lastVisibleRef.current) return;
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    dispatch({ type: "loadMoreStart" });
    try {
      const eventsRef = collection(firebaseDB, "Users", currentUid, "XPEvents");
      const q = query(
        eventsRef,
        orderBy("timestamp", "desc"),
        startAfter(lastVisibleRef.current),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      // Bail if uid changed mid-flight (sign-out, account switch). Without
      // this, the previous user's cursor and items would leak into the new
      // session's reducer state.
      if (uidRef.current !== currentUid) return;
      const items: XPEvent[] = [];
      snap.docs.forEach((d) => {
        const candidate = { ...d.data(), id: d.id };
        if (isXPEvent(candidate)) items.push(candidate);
      });
      const hasMore = snap.docs.length === pageSize;
      // Advance the cursor using the raw last doc, independent of validation
      // — otherwise a page where every doc fails isXPEvent would refuse to
      // advance and "Load older" would refetch the same invalid page on
      // every tap.
      const rawLastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      if (rawLastDoc) lastVisibleRef.current = rawLastDoc as DocumentSnapshot;
      hasMoreRef.current = hasMore;
      hasPaginatedRef.current = true;
      dispatch({ type: "loadMoreSuccess", items, hasMore });
    } catch (err) {
      if (uidRef.current !== currentUid) return;
      dispatch({
        type: "loadMoreError",
        error: err instanceof Error ? err : new Error(String(err)),
      });
    } finally {
      loadingMoreRef.current = false;
    }
  }, [pageSize]);

  return {
    status: state.status,
    items: state.items,
    error: state.error,
    hasMore: state.hasMore,
    loadingMore: state.loadingMore,
    loadMoreError: state.loadMoreError,
    loadMore,
  };
}

/**
 * Convert a Firestore Timestamp or Date into a Date object. Used by feed
 * consumers that need to format timestamps without checking the runtime shape.
 */
export function toEventDate(ts: Timestamp | Date | null | undefined): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof (ts as Timestamp).toDate === "function") return (ts as Timestamp).toDate();
  return null;
}
